import React, { useMemo } from 'react';
import { APITypes, MedicalDataTypes, Dynamo } from '../../../primary-record-types';
import { getIntl } from '../../../utils/intl';
import { FastField } from 'formik';
import { getDateFormatValidator, required } from '../../../utils/validators';
import ImagesField, { formValueImagesToResourceImageUpdates, resourceImagesToFormValueImages } from './ImagesField';
import { FormValueImage } from './ImagesField/types';
import { conditionStatusMessages } from '../../../utils/sharedMessages';
import { handleFormikSubmit } from '../../../utils/handleFormikSubmit';
import { isResourceActive } from '../../../utils/resourceUtils/isResourceActive';
import useRecordEvent from '../../../hooks/useRecordEvent';
import { FormStack } from '../../v2BaseComponents/Stack';
import { FormikInputs } from '../../v2BaseComponents/Inputs';
import { CenteredFormRow, Form, SplitFormRow } from '../../v2BaseComponents/Form';
import { DateTime } from 'luxon';

const intl = getIntl('conditionsForm', {
    save: 'Save',
    condition: 'Condition',
    notes: 'Additional Information',
    startDate: 'Start Date',
    endDate: 'End Date',
    status: 'Status',
    selectStatus: 'Select Status',
});

export type ConditionFormOwnProps = {
    isLoading: boolean;
    startDateTime?: string;
    defaultResource?: MedicalDataTypes.PrimaryRecordCondition;
    defaultImages?: Dynamo.ResourceImage[];
    onSubmit: (data: APITypes.medicalDataService.UpdateResourceBody<MedicalDataTypes.PrimaryRecordCondition>) => void;
    isDisabled: boolean;
    profileId: string;
    resourceId?: string;
    onCancel: () => void;
};

type FormValue = {
    resource: MedicalDataTypes.PrimaryRecordCondition;
    images: FormValueImage[];
};

const getCurrentDateValue = () => {
    return DateTime.now().toISODate();
};

const getDefaultFormValue = (startDateTime?: string): FormValue => ({
    resource: {
        id: '', //ignored
        version: '1',
        type: 'condition',
        name: '',
        startDateTime: startDateTime || getCurrentDateValue() || '',
        endDateTime: '',
        status: 'active',
        notes: '',
    },
    images: [],
});

export type ConditionFormProps = ConditionFormOwnProps;

const ConditionForm: React.FC<ConditionFormProps> = ({
    isLoading,
    onSubmit,
    isDisabled,
    defaultImages,
    defaultResource,
    startDateTime,
    profileId,
    resourceId,
    onCancel,
}) => {
    const initialValues = useMemo<FormValue>(
        () =>
            defaultResource && defaultImages
                ? {
                      resource: defaultResource,
                      images: resourceImagesToFormValueImages(defaultImages),
                  }
                : getDefaultFormValue(startDateTime),
        [defaultImages, defaultResource, startDateTime],
    );
    const recordEvent = useRecordEvent();

    return (
        <Form
            actionContainerSubmit
            initialValues={initialValues}
            onSubmit={(values, helpers) => {
                onSubmit({
                    resourceUpdate: {
                        ...values.resource,
                        startDateTime: values.resource.startDateTime || undefined,
                        endDateTime: isResourceActive(values.resource) ? undefined : values.resource.endDateTime,
                    },
                    imageUpdates: formValueImagesToResourceImageUpdates(values.images),
                });
                handleFormikSubmit(values, helpers);
                recordEvent(!!defaultResource ? 'editCondition' : 'createCondition');
            }}
            onCancel={onCancel}
            cancelButtonVariant="secondary"
        >
            {({ values, setFieldValue }) => {
                return (
                    <FormStack>
                        <SplitFormRow>
                            <FastField
                                component={FormikInputs.Input}
                                name="resource.name"
                                isRequired
                                title={intl('condition')}
                                isDisabled={isDisabled || isLoading}
                                validate={required}
                            />
                            <FastField
                                component={FormikInputs.TextArea}
                                name="resource.notes"
                                title={intl('notes')}
                                isDisabled={isDisabled || isLoading}
                            />
                        </SplitFormRow>
                        <SplitFormRow>
                            <FastField
                                name="resource.startDateTime"
                                title={intl('startDate')}
                                component={FormikInputs.DateTimePicker}
                                validate={getDateFormatValidator('MM/DD/YYYY')}
                            />
                            <FastField
                                name="resource.status"
                                title={intl('status')}
                                component={FormikInputs.Select}
                                selectOptions={(['active', 'inactive', 'chronic', 'ruled-out'] as const).map((_) => ({
                                    label: conditionStatusMessages(_),
                                    value: _,
                                }))}
                                placeholder={intl('selectStatus')}
                                validate={required}
                                dependentOnChange={(value: 'active' | 'inactive' | 'chronic' | 'ruled-out' | null) => {
                                    if (value === 'active') {
                                        setFieldValue('resource.endDateTime', '');
                                        setFieldValue('resource.startDateTime', getCurrentDateValue());
                                    }
                                }}
                            />
                        </SplitFormRow>
                        {isResourceActive(values.resource) ? null : (
                            <SplitFormRow>
                                <FastField
                                    name="resource.endDateTime"
                                    title={intl('endDate')}
                                    component={FormikInputs.DateTimePicker}
                                    validate={getDateFormatValidator('MM/DD/YYYY')}
                                />
                            </SplitFormRow>
                        )}
                        <CenteredFormRow>
                            <ImagesField
                                isDisabled={isDisabled || isLoading}
                                values={values.images}
                                resourceId={resourceId}
                                profileId={profileId}
                                category="condition"
                                setFieldValue={setFieldValue}
                            />
                        </CenteredFormRow>
                    </FormStack>
                );
            }}
        </Form>
    );
};

export default React.memo(ConditionForm);
